import React from 'react';
import classNames from 'classnames';
import {
  DragSource,
  DropTarget,
  DragSourceSpec,
  DropTargetSpec,
  ConnectDragSource,
  ConnectDropTarget,
  ConnectDragPreview,
  ConnectableElement,
  DragSourceMonitor,
} from 'react-dnd';
import { flow, throttle } from 'lodash';
import { findDOMNode } from 'react-dom';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Transition } from 'react-transition-group';
import withSizes from 'react-sizes';

import * as Types from 'types';
import { BREAKPOINTS, DRAGGABLE_TYPES } from 'constants/global';
import { newPlaylistArtwork } from 'features/playlists/helpers';
import { PlaylistArtworkPill } from 'playlists/components/PlaylistArtworkPill';
import { fadeAnimationForState } from 'services/theme';
import { onClickTracking } from 'utils/tracking/trackingHelpers';

const scroll = throttle(({ down }) => {
  const delta = down ? 100 : -100;
  if (down) {
    const isBottom =
      // @ts-ignore
      $(document).scrollTop() + $(window).height() >= $(document).height();
    if (isBottom) {
      return false;
    }
  } else {
    const isTop = $(document).scrollTop() == 0;
    if (isTop) {
      return false;
    }
  }

  window.scrollBy({
    top: delta,
    behavior: 'smooth',
  });
}, 400);

type HOCProps = {
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
  connectDragPreview: ConnectDragPreview;
  isLargeScreen: boolean;
};

type OwnProps = {
  onEndDrag: (props: Props, monitor: DragSourceMonitor) => void;
};

type Props = {
  playlistArtwork: Types.PlaylistArtwork;
  index: number;
  droppable: boolean;
  isDragging: boolean;
  className?: string;
  flashNotice?: string;
  showAdd?: boolean;
  canDrag?: boolean;
  moveArtwork: (dragIndex: number, hoverIndex: number) => void;
  insertArtwork: (playlistArtwork: Types.PlaylistArtwork, explicitInsertIndex?: number) => void;
  onPlayButtonClick: (playlistArtwork: Types.PlaylistArtwork) => void;
  onRemoveClick: (playlistArtworkToRemove: Types.PlaylistArtwork) => void;
  onAddClick: (playlistArtworkToAdd: Types.PlaylistArtwork) => void;
} & OwnProps &
  HOCProps;

class PlaylistArtwork extends React.Component<Props> {
  ref = React.createRef<HTMLDivElement>();
  mouseMoveHandlerAttached = false;

  constructor(props: Props) {
    super(props);

    this.handleMouseMove = this.handleMouseMove.bind(this);
  }

  handleMouseMove(event: React.MouseEvent) {
    let clientY;
    const height = $(window).height() || 0;

    if (event.type == 'touchmove') {
      // @ts-ignore
      clientY = event.targetTouches[0].clientY;
    } else {
      clientY = event.clientY;
    }

    if (clientY >= height * 0.9) {
      scroll({ down: true });
    } else if (clientY <= height * 0.1) {
      scroll({ down: false });
    }
  }

  handleScrollWhileDragging() {
    const { isDragging } = this.props;

    if (isDragging && !this.mouseMoveHandlerAttached) {
      this.listenMouseMove();
    }

    if (!isDragging && this.mouseMoveHandlerAttached) {
      this.stopListeningMouseMove();
    }
  }

  componentDidUpdate() {
    this.handleScrollWhileDragging();
  }

  componentDidMount() {
    this.handleScrollWhileDragging();

    // Disable HTML5 backend drag preview.
    this.props.connectDragPreview(getEmptyImage());
  }

  componentWillUnmount() {
    this.stopListeningMouseMove();
  }

  listenMouseMove() {
    if (!this.mouseMoveHandlerAttached) {
      this.mouseMoveHandlerAttached = true;
      // @ts-ignore
      $(window).on('mousemove touchmove', this.handleMouseMove);
    }
  }

  stopListeningMouseMove() {
    if (this.mouseMoveHandlerAttached) {
      this.mouseMoveHandlerAttached = false;
      // @ts-ignore
      $(window).off('mousemove touchmove', this.handleMouseMove);
    }
  }

  setRootAsDragSource(node: ConnectableElement) {
    return this.props.isLargeScreen ? this.props.connectDragSource(node) : node;
  }

  setDragHandleAsDragSource(node: ConnectableElement) {
    if (!this.props.droppable) {
      return null;
    }

    return this.props.isLargeScreen ? null : this.props.connectDragSource(node);
  }

  onClickHandler = (playlistArtwork: Types.PlaylistArtwork): void => {
    this.props.onAddClick(playlistArtwork);
    onClickTracking('add_to_playlist', 'playlist', `artwork_id: ${playlistArtwork.id}`);
  };

  render() {
    const { playlistArtwork, connectDropTarget, isDragging, canDrag } = this.props;
    const className = classNames(this.props.className, {
      'artwork-pill--dragging': isDragging,
      'artwork-pill--draggable': canDrag,
    });

    return this.setRootAsDragSource(
      connectDropTarget(
        <div ref={this.ref} className='pb-1'>
          <PlaylistArtworkPill
            playlistArtwork={playlistArtwork}
            onPlayButtonClick={this.props.onPlayButtonClick}
            className={className}
          >
            {this.setDragHandleAsDragSource(
              <button className='artwork-pill__handle icon-button icon-burger' />,
            )}
            {this.props.showAdd && (
              <button
                className='icon-button icon-thin-plus'
                onClick={() => this.onClickHandler(playlistArtwork)}
                title='Add'
                style={{ fontSize: 14, height: '100%' }}
              />
            )}
            <Transition
              appear
              in={!!this.props.flashNotice}
              timeout={1000}
              mountOnEnter
              unmountOnExit
            >
              {(state) => (
                <div
                  className={classNames(
                    'absolute-overlay bg-success text-white',
                    fadeAnimationForState(state),
                  )}
                >
                  <span className='translate-into-middle fs-16 nowrap'>
                    {this.props.flashNotice && (
                      <span className='icon-checkmark mr-mini' style={{ fontSize: 18 }} />
                    )}
                    {this.props.flashNotice}
                  </span>
                </div>
              )}
            </Transition>
          </PlaylistArtworkPill>
        </div>,
      ),
    );
  }
}

const playlistArtworkDropTarget: DropTargetSpec<Props> = {
  hover(props, monitor, component) {
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;

    if (!props.droppable) {
      return;
    }

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return;
    }

    const domElement = findDOMNode(component);
    if (!domElement || typeof domElement === 'string') {
      return null;
    }

    // Determine rectangle on screen
    const hoverBoundingRect = (domElement as Element).getBoundingClientRect();

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    if (!clientOffset) {
      return null;
    }

    // Get pixels to the top
    const cursorDistanceToHoverTop = clientOffset.y - hoverBoundingRect.top;

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    const draggingDownwards = dragIndex < hoverIndex;
    if (draggingDownwards && cursorDistanceToHoverTop < 10) {
      return;
    }

    // Dragging upwards
    if (!draggingDownwards && cursorDistanceToHoverTop > hoverBoundingRect.bottom - 10) {
      return;
    }

    if (monitor.getItem().placeholder) {
      // we're dragging an item which isn't in the playlist - we're inserting a new one
      props.insertArtwork(monitor.getItem(), hoverIndex);
    } else {
      // we're dragging an item already in playlist, we should move items around
      props.moveArtwork(dragIndex, hoverIndex);
    }

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    monitor.getItem().index = hoverIndex;
  },

  drop(props, _monitor, _component) {
    return props;
  },
};

const playlistArtworkDragSource: DragSourceSpec<Props, Types.PlaylistArtwork> = {
  /**
   * What you return is the only information available to the drop targets (and drag layer preview)
   * about the drag source so it's important to pick the minimal data
   * they need to know.
   */
  beginDrag: (props, _monitor, component) => {
    const beginDragBounds = component.ref.current.getBoundingClientRect();

    if (props.droppable) {
      return {
        ...props.playlistArtwork,
        _meta: { beginDragBounds },
      };
    }
    return newPlaylistArtwork(props.playlistArtwork.artwork, {
      placeholder: true,
      _meta: { beginDragBounds },
    });
  },

  endDrag: (props, monitor) => {
    props.onEndDrag(props, monitor);
  },

  canDrag: (props) => Boolean(props.canDrag),
};

export const PlaylistArtworkContainer = flow(
  DragSource(DRAGGABLE_TYPES.PLAYLIST_ARTWORK, playlistArtworkDragSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  })),
  DropTarget(DRAGGABLE_TYPES.PLAYLIST_ARTWORK, playlistArtworkDropTarget, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
  })),
  withSizes(({ width }) => ({
    isLargeScreen: width >= BREAKPOINTS.lg,
  })),
)(PlaylistArtwork);
